import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {NoteLinkedGroupKind, NoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {ContainedServerIdsFromClipboard} from 'notebook/notes/NoteConstants';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {
  FromServerNoteLinkedFile,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
  NoteLinkedFile,
  isNotePartWithItem,
} from 'notebook/util/NoteUtilConstants';

export const MIN_ITEM_WIDTH = 150;
export const MAX_ITEM_WIDTH = 400;

// Horizontal chrome around grid-contained images: 2 * (1px border + 4px padding) = 10px.
export const GRID_CONTAINED_IMAGE_PADDING = 10;

export interface NotePartGridGroupMetadata {}

export interface GridImageTask {
  taskId: string;
  fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>;
  fileName?: string;
}

export interface CreateNotePartGridGroupExtras {
  gridImageTasks?: Array<GridImageTask>;
}

export interface GridPlaceholderNotePart extends NotePart {
  _fileUploadDeferred?: JQueryPromise<FromServerNoteLinkedFile>;
  _fileName?: string;
}

export type NotePartGrid = NoteLinkedGroup<NotePartGridGroupMetadata>;

export function isNotePartGrid(noteLinkedObject: NoteLinkedObject): noteLinkedObject is NotePartGrid {
  return (
    noteLinkedObject.type === NoteLinkedObjectType.NOTE_LINKED_GROUP &&
    noteLinkedObject.kind === NoteLinkedGroupKind.NOTE_PART_GRID
  );
}

export function isImageFileNotePart(notePart: NotePart): notePart is NotePartWithItem<NoteLinkedFile> {
  return (
    isNotePartWithItem(notePart) &&
    isNoteLinkedFile(notePart.item) &&
    notePart.item.previewFile?.previewType === EXTERNAL_FILE_TYPE.IMAGE
  );
}

/**
 * Cross-entry NotePartGrid paste is intentionally limited to multi-image "Images" grids.
 * Supporting the other grid-backed note part variants should only require removing or
 * relaxing this guard once their contained note parts have an approved copy/paste contract.
 */
export function isMultiImageNotePartGrid(
  noteLinkedObject: NoteLinkedObject
): noteLinkedObject is NotePartGrid {
  if (!isNotePartGrid(noteLinkedObject)) {
    return false;
  }

  return (
    noteLinkedObject.containedNoteParts.length > 1 &&
    noteLinkedObject.containedNoteParts.every((notePart) => isImageFileNotePart(notePart))
  );
}

/**
 * Transient carrier for a multi-image grid's clipboard-embedded `{containedFileCid -> serverId}`
 * map. It bridges the cross-entry paste handoff between two files that never call each other:
 *
 *   1. `NoteLinkedGroupPlugin.itemFromHTML` parses the map off the clipboard HTML and `stash`es it
 *      onto the in-flight NotePart.
 *   2. `convertNotePartGridForPaste` `take`s it back out (which also strips it) and uses it as a
 *      fallback when the destination entry's `getIdFromCid` can't resolve a contained file.
 *
 * The field is underscore-prefixed and deliberately NOT part of the NotePart shape: it must never
 * reach the Redux store or serialization, which is why `take` always removes it. Centralizing the
 * key and the (un)wrapping here keeps that contract — and the otherwise-stringly-typed magic key —
 * in one place instead of duplicated, untyped casts across the two consumers.
 */
interface NotePartWithContainedServerIdsFromClipboard {
  _containedServerIdsFromClipboard?: ContainedServerIdsFromClipboard;
}

export function stashContainedServerIdsFromClipboard(
  notePart: NotePart,
  containedServerIds: ContainedServerIdsFromClipboard
): void {
  (notePart as NotePartWithContainedServerIdsFromClipboard)._containedServerIdsFromClipboard =
    containedServerIds;
}

/**
 * Reads and removes the transient clipboard id map from `notePart`. Always strips the field (even
 * when absent) so it can never be persisted, and returns `undefined` when no map was stashed.
 */
export function takeContainedServerIdsFromClipboard(
  notePart: NotePart
): ContainedServerIdsFromClipboard | undefined {
  const carrier = notePart as NotePartWithContainedServerIdsFromClipboard;
  const containedServerIds = carrier._containedServerIdsFromClipboard;
  delete carrier._containedServerIdsFromClipboard;
  return containedServerIds;
}
